/**
 * usePipelineProgress.ts
 *
 * Live-progress polling hook for /sources/reconcile (Pipeline Reconcile
 * dashboard). Polls GET /api/pipeline-reconcile/progress every
 * POLL_INTERVAL_MS while jobs are active (pending or running), and
 * auto-stops when the queue empties (server reports active: false).
 *
 * Read-only — this hook never enqueues/mutates worker_jobs itself. It only
 * polls a read-only endpoint. No websockets.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch } from '@/lib/apiFetch';

const POLL_INTERVAL_MS = 3000;

export interface StageProgress {
  jobType: string;
  label: string;
  pending: number;
  running: number;
  done: number;
  failed: number;
  total: number;
}

export interface TickerEntry {
  artifactId: string;
  meetingDate: string | null;
  meetingType: string | null;
  stage: string;
  outcome: string;
  detail: string | null;
  createdAt: string;
}

export interface PipelineProgressData {
  stages: StageProgress[];
  totals: {
    pending: number;
    running: number;
    done: number;
    failed: number;
    total: number;
  };
  active: boolean;
  ticker: TickerEntry[];
  refreshedAt: string;
}

export function usePipelineProgress() {
  const [data, setData] = useState<PipelineProgressData | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether the queue was ever active during this polling session,
  // so we can distinguish "never started" from "finished" for the
  // completion-summary UI.
  const [everActive, setEverActive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchOnce =
    useCallback(async (): Promise<PipelineProgressData | null> => {
      try {
        const res = await apiFetch('/api/pipeline-reconcile/progress');
        if (!res.ok) throw new Error(`pipeline-progress ${res.status}`);
        const json = (await res.json()) as { data: PipelineProgressData };
        setError(null);
        setData(json.data);
        if (json.data.active) setEverActive(true);
        return json.data;
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Failed to load pipeline progress'
        );
        return null;
      }
    }, []);

  const scheduleNext = useCallback(
    (result: PipelineProgressData | null) => {
      if (!pollingRef.current) return;
      // Auto-stop when the queue empties (nothing pending/running).
      if (!result || !result.active) {
        pollingRef.current = false;
        setPolling(false);
        return;
      }
      timerRef.current = setTimeout(() => {
        void fetchOnce().then(scheduleNext);
      }, POLL_INTERVAL_MS);
    },
    [fetchOnce]
  );

  const startPolling = useCallback(() => {
    if (pollingRef.current) return; // already polling
    pollingRef.current = true;
    setPolling(true);
    void fetchOnce().then(scheduleNext);
  }, [fetchOnce, scheduleNext]);

  const stopPolling = useCallback(() => {
    pollingRef.current = false;
    setPolling(false);
    clearTimer();
  }, [clearTimer]);

  const reset = useCallback(() => {
    stopPolling();
    setData(null);
    setEverActive(false);
    setError(null);
  }, [stopPolling]);

  useEffect(() => clearTimer, [clearTimer]);

  return {
    data,
    polling,
    error,
    everActive,
    startPolling,
    stopPolling,
    reset,
    refreshOnce: fetchOnce,
  };
}
